import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import validatePagerAddress from 'utils/validatePagerAddress';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  updateUserName,
  updateUserTimeFormat,
} from 'PREPAREsrc/store/user/user.action';
import { Form } from 'react-bootstrap';
import {
  useUpdateUserTimeFormatMutation,
  useUserTimeFormatQuery,
} from 'hooks/useUserTimeFormat';
import { useUpdateUserProfileSettingsMutation } from 'hooks/useUpdateUserProfileSettings';
import { useAors } from 'hooks/useAors';
import { useUpdateHomeAor } from 'hooks/useUpdateHomeAor';
import { updateUserProfileSettings } from 'actions/profileActions';
import { validatePhoneNumberRegex } from '../../utils/validatePhoneNumberRegex';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';

const timeFormatOptions = [
  {
    value: 'hour24Mode',
    label: '24 Hour Time Format',
  },
  {
    value: 'hour12Mode',
    label: '12 Hour Time Format',
  },
];

const validateAllProfileSettings = ({
  mobilePhoneNumber,
  homePhoneNumber,
  pagerAddress,
}: {
  [_key: string]: string;
}) => {
  if (
    mobilePhoneNumber &&
    mobilePhoneNumber.length &&
    !validatePhoneNumberRegex(mobilePhoneNumber)
  )
    return false;
  if (
    homePhoneNumber &&
    homePhoneNumber.length &&
    !validatePhoneNumberRegex(homePhoneNumber)
  )
    return false;
  if (
    pagerAddress &&
    pagerAddress.length &&
    !validatePagerAddress(pagerAddress)
  )
    return false;
  return true;
};

type ProfileSettingsProps = {
  user: User;
  focusedUser?: User;
};

const ProfileSettings: React.FC<ProfileSettingsProps> = ({
  user,
  focusedUser,
}) => {
  const prepareUserId = user.epic_user_id;

  const { user_guid: userGuid, profile_settings: profileSettings } = user;

  const form = useForm();
  const { register, setValue, formState, control, reset, watch, handleSubmit } =
    form;

  const reduxDispatch = useDispatch();

  const { data: timeFormat, refetch: refetchTimeFormat } =
    useUserTimeFormatQuery(prepareUserId);
  const updateTimeFormatMutation = useUpdateUserTimeFormatMutation();
  const updateUserProfileSettingsMutation =
    useUpdateUserProfileSettingsMutation();
  const { data: aors = [] } = useAors();
  const updateHomeAorMutation = useUpdateHomeAor();
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: roster } = useGetRostersForGroup(groupGuid);

  const currentUserRoster = roster?.find((r) => r.user_guid === userGuid);

  const aorOptions = aors.map((aor) => ({
    value: aor.id,
    label: aor.name,
  }));

  useEffect(() => {
    reset({
      userName: user.user_name,
      email: user.email_address,
      mobilePhoneNumber: profileSettings?.mobile_phone_number || '',
      homePhoneNumber: profileSettings?.home_phone_number || '',
      pagerAddress: profileSettings?.pager_address || '',
      optInForSms: Boolean(profileSettings?.opt_in_for_sms),
      timeFormat:
        timeFormatOptions.find((tF) => tF.value === timeFormat) ||
        timeFormatOptions[0],
      homeAor:
        aorOptions.find((a) => a.value === currentUserRoster?.home_aor) || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profileSettings, timeFormat, reset, aors, currentUserRoster]);

  const saveProfileSettingsClicked = async (formValues: any) => {
    const newSettings = {
      ...profileSettings,
      userName: formValues.userName,
      mobile_phone_number: formValues.mobilePhoneNumber,
      home_phone_number: formValues.homePhoneNumber,
      pager_address: formValues.pagerAddress,
      opt_in_for_sms: formValues.optInForSms,
    };

    await updateUserProfileSettingsMutation.mutateAsync({
      userId: userGuid,
      newSettings,
    });

    await updateHomeAorMutation.mutateAsync({
      homeAor: formValues.homeAor?.value || null,
    });
    if (!focusedUser) {
      reduxDispatch(updateUserProfileSettings(userGuid, newSettings));
    }

    await updateTimeFormatMutation.mutateAsync({
      userId: prepareUserId,
      timeFormat: formValues.timeFormat?.value,
      name: formValues.userName,
    });

    if (!focusedUser) {
      reduxDispatch(updateUserName(formValues.userName));
      reduxDispatch(updateUserTimeFormat(formValues.timeFormat.value));
      refetchTimeFormat();
    }
    toast.success('User Data Updated Successfully');
  };

  const areAllProfileSettingsValid = validateAllProfileSettings({
    mobilePhoneNumber: watch('mobilePhoneNumber'),
    homePhoneNumber: watch('homePhoneNumber'),
    pagerAddress: watch('pagerAddress'),
  });

  return (
    <FormProvider {...form}>
      <Form onSubmit={handleSubmit(saveProfileSettingsClicked)}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Name</label>
            <StylishNewInput {...register('userName')} />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Email</label>
            <StylishNewInput {...register('email')} disabled readOnly />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Phone (Mobile)</label>
            <StylishNewInput {...register('mobilePhoneNumber')} />
            {watch('mobilePhoneNumber') &&
              watch('mobilePhoneNumber').length &&
              !validatePhoneNumberRegex(watch('mobilePhoneNumber')) && (
                <span className="form-text form-error">
                  Invalid mobile phone number.
                </span>
              )}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Phone (Home)</label>
            <StylishNewInput {...register('homePhoneNumber')} />
            {watch('homePhoneNumber') &&
              watch('homePhoneNumber').length &&
              !validatePhoneNumberRegex(watch('homePhoneNumber')) && (
                <span className="form-text form-error">
                  Invalid home phone number.
                </span>
              )}
          </div>
          <div className="col-md-6 mb-3 mb-md-0">
            <label className="form-label">Pager Address</label>
            <StylishNewInput {...register('pagerAddress')} />
            {watch('pagerAddress') &&
              watch('pagerAddress').length &&
              !validatePagerAddress(watch('pagerAddress')) && (
                <span className="form-text form-error">
                  Invalid pager address.
                </span>
              )}
          </div>
          <div className="col-md-6 d-flex gap-3">
            <div className="col-md-6">
              <label className="form-label">Home AOR</label>
              <Controller
                control={control}
                name="homeAor"
                render={({ field: { onChange, value } }) => (
                  <StylishNewSelect
                    options={aorOptions}
                    defaultValue={value}
                    value={value}
                    onChange={onChange}
                    placeholder={'Select Home AOR'}
                    isClearable={true}
                    isSearchable={true}
                    isMulti={false}
                    isDisabled={false}
                  />
                )}
              />
            </div>
            <div className="col-md-6 pe-3">
              <label className="form-label text-truncate d-block">
                Preferred Exercise Time Format
              </label>
              <Controller
                control={control}
                name="timeFormat"
                render={({ field: { onChange, value } }) => (
                  <StylishNewSelect
                    options={timeFormatOptions}
                    defaultValue={value}
                    value={value}
                    onChange={onChange}
                    placeholder={'Select Preferred Exercise Time Format'}
                    isClearable={false}
                    isSearchable={true}
                    isMulti={false}
                    isDisabled={false}
                  />
                )}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 mb-3">
            <strong>
              Consent for Communication: By entering your phone number(s) here,
              you are giving explicit consent to receive automated messages from
              Pratus related to active hazard alerts and incident response
              activations. This may include SMS messages, emails, and
              text-to-speech calls. You understand that this consent is a
              condition of your use of Pratus services and your employment
              requirements.
            </strong>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 mb-3">
            <Form.Switch
              id="sms-consent-switch"
              label="Opt In to SMS Alerts"
              {...register('optInForSms')}
              checked={watch('optInForSms')}
              onChange={(e) => setValue('optInForSms', e.target.checked)}
            />
          </div>
        </div>
        <div className="button-group justify-content-end mt-4">
          <StylishNewButton
            className={'button--primary'}
            type="submit"
            disabled={!areAllProfileSettingsValid}
          >
            Save
          </StylishNewButton>
        </div>
        {Object.keys(formState.errors).length > 0 && (
          <span className="form-text form-error multiline mt-3">
            Profile settings invalid. Please correct.
          </span>
        )}
      </Form>
    </FormProvider>
  );
};

export default ProfileSettings;
